import { Inject, inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  cloneDeep,
  divide,
  filter,
  find,
  get,
  includes,
  isEqual,
  isObject,
  map,
  mapValues,
  multiply,
  round,
  set,
  sortBy,
  sum,
  toNumber,
  values,
} from 'lodash';
import {
  BehaviorSubject,
  first,
  map as rxMap,
  Observable,
  startWith,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs';

import {
  Application,
  ApplicationStepState,
  Contact,
  RoleType,
} from '@lockton-apps/shared/interfaces';
import {
  DynamicFormHooks,
  DynamicFormParsers,
  DynamicFormsExtensionsService,
  SelectOption,
} from '@lockton-apps/dynamic-forms';
import { LsosRenewalsApplicationActions } from '@lockton-apps/lsos-renewals/action';
import { PROPOSAL_PARTNER_DATA } from '../../constants/proposal-partner-data';
import { FormGroup } from '@angular/forms';
import { CastObservablePipe } from '@lockton-apps/shared/ui/pipes';
import {
  DocumentsService,
  SupportingDocumentsService,
} from '@lockton-apps/shared/data-access/documents';
import { FeedbackService } from '@lockton-apps/shared/ui/lui';

type PrincipalValue = {
  name: string;
  status: string;
  qualification: string;
  startDate: string;
  endDate: string | null;
};

interface PartnerData {
  application: Application;
  clientId: number;
  contactsData: {
    contactsLoaded: boolean;
    allContacts: Contact[];
  };
  isPrimaryContact: boolean;
  role: RoleType;
}

@Injectable()
export class LsosDynamicFormsExtensionsService extends DynamicFormsExtensionsService {
  private readonly store = inject(Store);

  constructor(
    @Inject(PROPOSAL_PARTNER_DATA)
    protected readonly partnerData$: Observable<PartnerData>,
    castObservable: CastObservablePipe,
    documentsService: DocumentsService,
    supportingDocumentsService: SupportingDocumentsService,
    feedbackService: FeedbackService
  ) {
    super(
      castObservable,
      documentsService,
      supportingDocumentsService,
      feedbackService
    );
  }

  cachedFeeValue: any;

  private partnerOptions$ = new BehaviorSubject<SelectOption[]>([]);

  override readonly extensions = [
    {
      id: 'contactOptions',
      prop: 'options',
      value: this.partnerData$.pipe(
        rxMap(
          ({ contactsData: { contactsLoaded, allContacts }, application }) => {
            if (!contactsLoaded) {
              return [{ label: 'Loading...', value: null, disabled: true }];
            }

            const primaryContact = get(
              application.data,
              'aboutYourCompany.keyContacts.primary'
            );
            const options = sortBy(
              map(allContacts, (contact: Contact) => ({
                label: contact.displayName,
                value: contact.displayName,
                description: contact.email,
              })),
              'label'
            );

            if (primaryContact) {
              options.unshift({
                label: primaryContact.name,
                description: primaryContact.email,
                value: primaryContact.name,
              });
            }

            return options;
          }
        )
      ),
    },
    {
      id: 'partnerOptions',
      prop: 'options',
      value: this.partnerOptions$,
    },
  ];

  override readonly hooks: DynamicFormHooks[] = [
    {
      id: 'contactSelected',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.valueChanges
            .pipe(
              withLatestFrom(
                field.props!['options'] as Observable<SelectOption[]>
              ),
              takeUntil(this.destroyNotifier)
            )
            .subscribe(([value, options]) => {
              const option: SelectOption | undefined = find(options, { value });

              if (option) {
                (field.formControl!.parent as FormGroup)!.patchValue({
                  email: option.description,
                });
              }
            });
        },
      },
    },
    {
      id: 'partnersHook',
      hookConfig: {
        onInit: (field) => {
          field.formControl?.valueChanges
            .pipe(
              startWith(field.formControl.value),
              tap((value) => {
                const allPrincipals = value;

                const commonPrincipals = filter(
                  allPrincipals,
                  (principal) =>
                    isObject(principal) &&
                    'status' in principal &&
                    'name' in principal &&
                    principal.name &&
                    includes(['DP', 'CP'], principal.status)
                ) as PrincipalValue[];

                const options = map(commonPrincipals, (option) => ({
                  label: option.name,
                  value: option.name,
                }));

                this.partnerOptions$.next(options);
              }),
              takeUntil(this.destroyNotifier)
            )
            .subscribe();
        },
      },
    },
    {
      id: 'feeHook',
      hookConfig: {
        onInit: (field) => {
          this.cachedFeeValue = cloneDeep(
            get(field.model, 'feeIncomeSourceOfFees')
          );
        },
        onDestroy: (field) => {
          this.partnerData$.pipe(first()).subscribe(({ application }) => {
            const model = cloneDeep(get(field.model, 'feeIncomeSourceOfFees'));
            if (isEqual(this.cachedFeeValue, model) || !application) {
              return;
            }
            const steps = map(application.steps, (step) => {
              return step.id !== 'areas-of-practice'
                ? step
                : { ...step, state: ApplicationStepState.Invalid };
            });

            this.store.dispatch(
              LsosRenewalsApplicationActions.updateStepsState({ steps })
            );
          });
        },
      },
    },
  ];

  override readonly parsers: DynamicFormParsers[] = [
    {
      id: 'breakdown',
      parsers: [
        (model: unknown) => {
          if (isObject(model)) {
            const propConv = toNumber(
              get(model, 'propertyConveyancing._declaredValue')
            );
            const breakdownTotal = sum(
              values(mapValues(model, '_declaredValue'))
            );

            const estateAgency = toNumber(
              get(model, 'propertyConveyancing.estateAgency')
            );

            const propConvPer = this.percent(propConv, breakdownTotal);

            const estateAgencyPercent = this.percent(estateAgency, propConv);

            set(model, 'derived.breakdownTotal', breakdownTotal);
            set(model, 'derived.propConvPer', propConvPer);
            set(model, 'derived.estateAgencyPer', estateAgencyPercent);
          }
          return model;
        },
      ],
    },
  ];

  private percent(value: number, total: number): number {
    return round(multiply(divide(value, total), 100), 2) || 0;
  }
}
